import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import colors from "../../../theme/colors";
import TdCell from "../../../components/forms/TdCell";

type Props = {
  field: ConditionUIField;
  value?: string;
  ancestors?: NonNullable<ConditionUIField["enabled_by"]>[];
};

/**
 * Evaluates whether a single dependency link is in a "hidden" state
 * based on the current form value of that dependency.
 */
const isDependencyHidden = (
  dep: NonNullable<ConditionUIField["enabled_by"]>,
  depState: any
): boolean => {
  if (dep.id.includes("engineer_flag")) return false;

  const allChecked = !!depState === true;
  const allUnchecked = !!depState === false;

  if (dep.conditional_val && depState) {
    return typeof depState === "string"
      ? depState !== dep.conditional_val
      : Object.values(depState).every(
          (val) => val !== dep.conditional_val
        );
  }

  if (dep.conditional_vals && depState) {
    return typeof depState === "string"
      ? !dep.conditional_vals.includes(depState)
      : Object.values(depState).every(
          (val) => !dep.conditional_vals!.includes(val as string)
        );
  }

  return dep.neg ? allChecked : allUnchecked;
};

const TeardownField = ({ field, value, ancestors = [] }: Props) => {
  const { control } = useFormContext();

  // Watch all ancestor fields in the dependency chain so that disabling
  // a top-level field also hides all downstream nested fields.
  const ancestorIds = useMemo(
    () => ancestors.map((a) => a.id),
    [ancestors]
  );

  const ancestorStates = useWatch({
    control,
    name: ancestorIds,
    disabled: ancestorIds.length === 0,
  });

  // The field is hidden if ANY ancestor in the chain is disabled
  const rowDependencyHidden = useMemo(() => {
    if (ancestors.length === 0) return false;

    return ancestors.some((dep, index) => {
      const depState = Array.isArray(ancestorStates)
        ? ancestorStates[index]
        : ancestorStates;
      return isDependencyHidden(dep, depState);
    });
  }, [ancestors, ancestorStates]);

  return (
    <div style={rowDependencyHidden ? { display: "none" } : {}}>
      <Box mt={6} mb={2} display="flex" alignItems="flex-end">
        <Typography color="textSecondary" className="small">
          {field.label}{" "}
          {field.required ? (
            <span style={{ color: colors.accent.red }}>*</span>
          ) : null}
        </Typography>
      </Box>

      <Controller
        name={field.id}
        control={control}
        defaultValue={value}
        rules={{
          validate: (value) => {
            if (field.required) {
              if (field.type.includes("*")) {
                return rowDependencyHidden || !value.includes(null);
              } else if (field.type === "datetime") {
                return value !== null;
              }
              return rowDependencyHidden || value !== null;
            } else {
              return true;
            }
          },
        }}
        render={({
          field: { onChange, onBlur, value, name, ref },
          fieldState: { invalid },
        }) => (
          <TdCell
            ref={ref}
            noTable
            name={name}
            uiField={field}
            conditionKey=""
            onBlur={onBlur}
            onChange={onChange}
            value={value}
            error={invalid}
          />
        )}
      />
    </div>
  );
};

export default TeardownField;
